import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, MessageSquare, Paperclip, Download, Eye, Send, Trash2, FileText, Image as ImageIcon, LogOut, Lock, Unlock, ClipboardCheck, CheckCircle2, XCircle, Trophy } from "lucide-react";
import { toast } from "sonner";
import BadgeCelebration from "@/components/BadgeCelebration";
import AttachmentPreview from "@/components/AttachmentPreview";
import { fetchNewBadges, markBadgesSeen } from "@/lib/badges";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SECTION_LABEL = { quantitative: "قدرات كمية", verbal: "قدرات لفظية" };

function AttachmentCard({ a, onPreview }) {
    const isImg = a.mime?.startsWith("image/");
    const isPdf = a.mime === "application/pdf";
    const viewUrl = `${BACKEND}${a.url}`;
    const downloadUrl = `${BACKEND}${a.url}?download=1`;
    const previewable = isImg || isPdf;
    return (
        <div className="border border-slate-200 rounded-lg p-3 flex items-center gap-3 bg-slate-50">
            {isImg ? (
                <button
                    type="button"
                    onClick={() => onPreview(a)}
                    data-testid={`attach-thumb-${a.id}`}
                    className="w-14 h-14 rounded overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1B2A4E]"
                    title="معاينة"
                >
                    <img src={viewUrl} alt={a.filename} className="w-full h-full object-cover" loading="lazy" />
                </button>
            ) : (
                <div className="w-14 h-14 rounded bg-white text-[#1B2A4E] flex items-center justify-center"><FileText className="w-5 h-5" /></div>
            )}
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{a.filename}</div>
                <div className="text-xs text-slate-500 font-mono-num" dir="ltr">{a.mime}</div>
            </div>
            <div className="flex gap-1">
                {previewable && (
                    <button
                        type="button"
                        onClick={() => onPreview(a)}
                        data-testid={`attach-view-${a.id}`}
                        className="p-2 rounded hover:bg-slate-200 text-[#1B2A4E]"
                        title="معاينة"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                )}
                <a href={downloadUrl} download={a.filename} data-testid={`attach-download-${a.id}`} className="p-2 rounded hover:bg-slate-200 text-[#1B2A4E]" title="تحميل">
                    <Download className="w-4 h-4" />
                </a>
            </div>
        </div>
    );
}

function QuizTaker({ topic, user, onDone, onNewBadges }) {
    const questions = topic.quiz?.questions || [];
    const [answers, setAnswers] = useState(Array(questions.length).fill(-1));
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [attempts, setAttempts] = useState([]);

    useEffect(() => {
        if (user?.role === "student") api.get(`/topics/${topic.id}/quiz/attempts`).then((r) => setAttempts(r.data)).catch(() => {});
    }, [topic.id, user?.role]);

    const submit = async () => {
        if (answers.some((a) => a < 0)) return toast.error("أجيبي على جميع الأسئلة");
        setBusy(true);
        try {
            const { data } = await api.post(`/topics/${topic.id}/quiz/submit`, { answers });
            setResult(data);
            api.get(`/topics/${topic.id}/quiz/attempts`).then((r) => setAttempts(r.data));
            // Fetch any newly earned badges and celebrate immediately
            if (user?.role === "student") {
                fetchNewBadges(user.id).then((ids) => onNewBadges && onNewBadges(ids));
            }
            onDone && onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    if (result) {
        const pct = result.score;
        const good = pct >= 60;
        return (
            <div className="space-y-3">
                <div className={`rounded-2xl p-6 text-center ${good ? "bg-emerald-50 border border-emerald-200" : "bg-[#FFF1F2] border border-[#FECACA]"}`}>
                    <Trophy className={`w-12 h-12 mx-auto mb-2 ${good ? "text-emerald-600" : "text-[#C8102E]"}`} />
                    <div className="text-4xl font-bold font-mono-num text-[#0F172A]">{pct}%</div>
                    <div className={`text-sm mt-1 ${good ? "text-emerald-700" : "text-[#C8102E]"}`}>{result.correct_count} من {result.total} إجابة صحيحة</div>
                </div>
                <div className="space-y-2">
                    {result.per_question.map((r, i) => (
                        <div key={i} className={`flex items-start gap-2 p-3 rounded-lg border ${r.is_correct ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-[#FFF1F2]/60"}`}>
                            {r.is_correct ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <XCircle className="w-5 h-5 text-[#C8102E] shrink-0" />}
                            <div className="text-sm flex-1">
                                <div className="font-medium">س{i + 1}: {questions[i].text}</div>
                                <div className="text-xs text-slate-600 mt-1">إجابتك: <span className={r.is_correct ? "text-emerald-700" : "text-[#C8102E]"}>{questions[i].choices[r.given]}</span></div>
                                {!r.is_correct && <div className="text-xs text-emerald-700 mt-0.5">الإجابة الصحيحة: {questions[i].choices[r.correct_index]}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {attempts.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                    محاولة سابقة: <span className="font-mono-num font-bold">{attempts[0].score}%</span> ({attempts[0].correct_count}/{attempts[0].total})
                </div>
            )}
            {questions.map((q, qi) => (
                <div key={q.id} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-3">
                        <Badge className="bg-[#1B2A4E] text-white hover:bg-[#1B2A4E] font-mono-num">{qi + 1}</Badge>
                        <div className="font-medium flex-1">{q.text}</div>
                    </div>
                    <div className="space-y-2 pr-8">
                        {q.choices.map((c, ci) => (
                            <label key={ci} className={`flex items-center gap-2 rounded-lg p-2 border cursor-pointer transition-all ${answers[qi] === ci ? "border-[#1B2A4E] bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
                                <input type="radio" name={`q-${qi}`} checked={answers[qi] === ci} onChange={() => setAnswers((a) => a.map((x, i) => i === qi ? ci : x))} className="w-4 h-4 accent-[#1B2A4E]" data-testid={`quiz-q${qi}-c${ci}`} />
                                <span className="text-sm">{c}</span>
                            </label>
                        ))}
                    </div>
                </div>
            ))}
            <Button onClick={submit} disabled={busy} data-testid="btn-submit-quiz" className="w-full h-11 bg-[#1B2A4E] hover:bg-[#0F172A]">
                {busy ? "جارٍ التصحيح..." : "إرسال الإجابات"}
            </Button>
        </div>
    );
}

function TrainerQuizStats({ topicId }) {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get(`/topics/${topicId}/quiz/attempts`).then((r) => setRows(r.data)).catch(() => {}); }, [topicId]);
    const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.score, 0) / rows.length) : 0;
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold font-mono-num text-[#1B2A4E]">{rows.length}</div><div className="text-xs text-slate-500">محاولات</div></div>
                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold font-mono-num text-emerald-700">{avg}%</div><div className="text-xs text-slate-500">متوسط النتيجة</div></div>
            </div>
            <div className="space-y-1 max-h-80 overflow-auto">
                {rows.length === 0 && <div className="text-center text-slate-400 text-sm py-4">لا توجد محاولات بعد</div>}
                {rows.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm border border-slate-100 rounded p-2">
                        <span className="font-medium">{r.student_name}</span>
                        <div className="flex items-center gap-2">
                            <Badge className={r.score >= 60 ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-red-100 text-red-800 hover:bg-red-100"}>{r.score}%</Badge>
                            <span className="text-xs text-slate-400 font-mono-num" dir="ltr">{new Date(r.submitted_at).toLocaleDateString("ar-EG")}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function TopicView() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user, logout } = useAuth();
    const [topic, setTopic] = useState(null);
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [newBadges, setNewBadges] = useState([]);
    const [previewAttachment, setPreviewAttachment] = useState(null);

    const load = async () => {
        try {
            const [t, c] = await Promise.all([api.get(`/topics/${id}`), api.get(`/topics/${id}/comments`)]);
            setTopic(t.data); setComments(c.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const send = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try { await api.post(`/topics/${id}/comments`, { content: text.trim() }); setText(""); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const del = async (cid) => {
        if (!window.confirm("حذف التعليق؟")) return;
        try { await api.delete(`/topics/${id}/comments/${cid}`); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    const lockToggle = async () => {
        try { await api.patch(`/topics/${id}`, { comments_locked: !topic.comments_locked }); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    const back = () => {
        if (user?.role === "trainer") return nav("/trainer/topics");
        if (topic) return nav(`/student/section/${topic.section}`);
        return nav(-1);
    };

    if (!topic) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">جاري التحميل...</div>;

    const canDelete = (c) => user?.role === "admin" || (user?.role === "trainer" && topic.trainer_id === user.id) || c.author_id === user?.id;
    const canComment = user?.role !== "parent" && user?.role !== "admin" && !topic.comments_locked;
    const isOwner = user?.role === "trainer" && topic.trainer_id === user.id;
    const cleanHtml = DOMPurify.sanitize(topic.content_html || "", {
        ADD_TAGS: ["img"],
        ADD_ATTR: ["src", "alt", "title", "width", "height", "class", "style", "loading", "crossorigin", "referrerpolicy"],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|data|blob):|\/)/i,
    });
    const hasQuiz = topic.quiz && topic.quiz.questions?.length > 0;

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <BadgeCelebration
                badgeIds={newBadges}
                onDismiss={(ids) => { markBadgesSeen(user?.id, ids); setNewBadges([]); }}
            />
            <AttachmentPreview
                attachment={previewAttachment}
                open={!!previewAttachment}
                onOpenChange={(o) => { if (!o) setPreviewAttachment(null); }}
            />
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={back} className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> رجوع</Button>
                        <BrandLogo size={36} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4 ml-1" /> خروج</Button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <Badge className={topic.section === "quantitative" ? "bg-[#1B2A4E] text-white hover:bg-[#1B2A4E]" : "bg-[#C8102E] text-white hover:bg-[#C8102E]"}>{SECTION_LABEL[topic.section]}</Badge>
                        {topic.attachments?.length > 0 && <Badge variant="outline"><Paperclip className="w-3 h-3 ml-0.5" /> {topic.attachments.length} مرفق</Badge>}
                        {hasQuiz && <Badge variant="outline" className="text-[#C8102E] border-[#FECACA]"><ClipboardCheck className="w-3 h-3 ml-0.5" /> اختبار</Badge>}
                        {topic.comments_locked && <Badge variant="outline" className="text-amber-700 border-amber-300"><Lock className="w-3 h-3 ml-0.5" /> التعليقات مقفلة</Badge>}
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">{topic.title}</h1>
                    <div className="flex items-center gap-3 mb-6">
                        <Avatar url={topic.trainer_avatar} name={topic.trainer_name} size={40} />
                        <div>
                            <div className="text-sm font-semibold text-[#0F172A]">{topic.trainer_name}</div>
                            <div className="text-xs text-slate-500 font-mono-num" dir="ltr">{new Date(topic.created_at).toLocaleString("ar-EG")}</div>
                        </div>
                    </div>
                    <div className="tiptap-content prose max-w-none text-[#0F172A]" dangerouslySetInnerHTML={{ __html: cleanHtml }} />

                    {topic.attachments?.length > 0 && (
                        <div className="mt-6 space-y-2">
                            <div className="text-sm font-semibold text-[#0F172A]">المرفقات</div>
                            {topic.attachments.map((a) => <AttachmentCard key={a.id} a={a} onPreview={setPreviewAttachment} />)}
                        </div>
                    )}

                    {isOwner && (
                        <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
                            <Button variant="outline" size="sm" onClick={lockToggle}>
                                {topic.comments_locked ? <><Unlock className="w-4 h-4 ml-1" /> فتح التعليقات</> : <><Lock className="w-4 h-4 ml-1" /> إغلاق التعليقات</>}
                            </Button>
                        </div>
                    )}
                </div>

                {hasQuiz && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <ClipboardCheck className="w-5 h-5 text-[#C8102E]" />
                            <div className="font-semibold text-[#0F172A]">اختبار الموضوع ({topic.quiz.questions.length} أسئلة)</div>
                        </div>
                        {user?.role === "student" && <QuizTaker topic={topic} user={user} onNewBadges={setNewBadges} />}
                        {(user?.role === "trainer" || user?.role === "admin") && <TrainerQuizStats topicId={topic.id} />}
                        {user?.role === "parent" && <div className="text-sm text-slate-500">هذا الاختبار مخصص للطالبات فقط.</div>}
                    </div>
                )}

                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-4"><MessageSquare className="w-4 h-4 text-[#1B2A4E]" /><div className="font-semibold text-[#0F172A]">التعليقات ({comments.length})</div></div>
                    <div className="space-y-3 mb-4">
                        {comments.length === 0 && <div className="text-center text-slate-400 text-sm py-4">لا توجد تعليقات</div>}
                        {comments.map((c) => (
                            <div key={c.id} data-testid={`comment-${c.id}`} className={`rounded-lg p-3 flex gap-3 ${c.author_role === "trainer" ? "bg-slate-50 border-r-2 border-[#1B2A4E]" : "bg-white border border-slate-100"}`}>
                                <Avatar url={c.author_avatar} name={c.author_name} size={36} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-sm text-[#0F172A]">{c.author_name}</span>
                                            <Badge variant="outline" className="text-[10px]">{c.author_role === "trainer" ? "مدربة" : c.author_role === "student" ? "طالبة" : c.author_role}</Badge>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-slate-400 font-mono-num" dir="ltr">{new Date(c.created_at).toLocaleString("ar-EG")}</span>
                                            {canDelete(c) && <button onClick={() => del(c.id)} className="text-[#C8102E] p-1 rounded hover:bg-[#FFF1F2]"><Trash2 className="w-3.5 h-3.5" /></button>}
                                        </div>
                                    </div>
                                    <div className="text-sm text-[#0F172A] whitespace-pre-wrap">{c.content}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {canComment && (
                        <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
                            <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="اكتبي تعليقاً..." rows={1} className="resize-none min-h-11" data-testid="comment-input" />
                            <Button onClick={send} disabled={busy || !text.trim()} className="bg-[#1B2A4E] hover:bg-[#0F172A] h-11 px-4"><Send className="w-4 h-4" /></Button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
